import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Cpu, Battery, Wifi, Activity, Award, ChevronLeft, ChevronRight, Check, X, Zap, Brain, Users, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

const Product: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState<'lite' | 'pro'>('lite');

  const productImages = [
    '/product/shin-guard-1.jpg',
    '/product/shin-guard-2.jpg',
    '/product/shin-guard-3.jpg',
    '/product/shin-guard-4.jpg'
  ];

  const products = {
    lite: {
      name: 'Champion Lite',
      description: 'Perfect for players looking to upgrade from basic shin guards to smart football analytics',
      features: [
        'Basic physical data tracking',
        'Football statistics (passes, shots, touches)',
        'Mobile app access',
        'Community features',
        '2-week battery life',
        'Water resistant'
      ],
      metrics: '25+ performance metrics',
      color: 'from-cresento-secondary to-green-600'
    },
    pro: {
      name: 'Champion Pro',
      description: 'Professional-grade analytics for serious athletes and teams',
      features: [
        'All Champion Lite features',
        '50+ advanced metrics',
        'AI-based performance analysis',
        'Real-time coaching feedback',
        'TeamHub+ integration',
        'Injury prevention alerts',
        'Extended 3-week battery',
        'Premium support'
      ],
      metrics: '50+ performance metrics',
      color: 'from-cresento-accent to-yellow-500'
    }
  };

  const comparisonData = [
    { feature: 'AI Coaching', cresento: true, playermaker: false, nikeAdidas: false },
    { feature: 'Real-Time Data', cresento: true, playermaker: 'Limited', nikeAdidas: false },
    { feature: 'Performance Metrics', cresento: '50+', playermaker: '15-20', nikeAdidas: 'None' },
    { feature: 'Battery Life', cresento: '2-3 weeks', playermaker: '1 week', nikeAdidas: 'N/A' },
    { feature: 'Injury Prevention', cresento: true, playermaker: false, nikeAdidas: false },
    { feature: 'Team Analytics', cresento: true, playermaker: 'Basic', nikeAdidas: false },
    { feature: 'Mobile App', cresento: true, playermaker: true, nikeAdidas: false }
  ];

  const futureProducts = [
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Rugby Edition',
      description: 'Coming Q2 2025',
      status: 'In Development'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Basketball Sensors',
      description: 'Coming Q3 2025',
      status: 'Planning Phase'
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: 'Health Tech Integration',
      description: 'Coming Q4 2025',
      status: 'Research Phase'
    }
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-cresento-primary to-cresento-dark text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Cresento Smart Shinpads</h1>
            <p className="text-xl md:text-2xl text-cresento-light mb-4">
              AI-Powered Performance Analytics for Every Player
            </p>
            <p className="text-lg md:text-xl text-gray-400 mb-8">
              Why settle for cheap shinpads, plastic shinpads, or non-tech shinpads when you can have professional-grade AI analytics? Move beyond basic shin guards to revolutionary smart technology.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Product Selection */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-cresento-accent mb-4">Choose Your Champion</h2>
            <p className="text-xl text-cresento-light">Select the perfect model for your performance goals. No more discount shinpads or low-quality shinpads – choose professional-grade smart technology.</p>
          </motion.div>

          {/* Product Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
            {/* Champion Lite */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className={`relative rounded-2xl overflow-hidden cursor-pointer transition-all ${
                selectedProduct === 'lite' ? 'ring-4 ring-cresento-accent transform scale-105' : ''
              }`}
              onClick={() => setSelectedProduct('lite')}
            >
              <div className={`bg-gradient-to-br ${products.lite.color} p-8`}>
                <div className="glass-strong rounded-lg p-6">
                  <h3 className="text-3xl font-bold text-white mb-2">{products.lite.name}</h3>
                  <p className="text-2xl font-bold text-white mb-4">{products.lite.metrics}</p>
                  <p className="text-white/90 mb-6">{products.lite.description}</p>
                  <ul className="space-y-2 mb-6">
                    {products.lite.features.slice(0, 6).map((feature, index) => (
                      <li key={index} className="flex items-center text-white/90">
                        <Check className="w-5 h-5 mr-2 text-white" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link 
                    to="/level-up"
                    className="w-full bg-white text-cresento-secondary hover:bg-gray-100 font-bold py-3 rounded-lg transition-all inline-flex items-center justify-center"
                  >
                    Sign Up for Updates
                  </Link>
                </div>
              </div>
              {selectedProduct === 'lite' && (
                <div className="absolute top-4 right-4 bg-cresento-accent text-black px-3 py-1 rounded-full text-sm font-semibold">
                  Selected
                </div>
              )}
            </motion.div>

            {/* Champion Pro */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className={`relative rounded-2xl overflow-hidden cursor-pointer transition-all ${
                selectedProduct === 'pro' ? 'ring-4 ring-cresento-accent transform scale-105' : ''
              }`}
              onClick={() => setSelectedProduct('pro')}
            >
              <div className={`bg-gradient-to-br ${products.pro.color} p-8`}>
                <div className="glass-strong rounded-lg p-6">
                  <h3 className="text-3xl font-bold text-white mb-2">{products.pro.name}</h3>
                  <p className="text-2xl font-bold text-white mb-4">{products.pro.metrics}</p>
                  <p className="text-white/90 mb-6">{products.pro.description}</p>
                  <ul className="space-y-2 mb-6">
                    {products.pro.features.slice(0, 6).map((feature, index) => (
                      <li key={index} className="flex items-center text-white/90">
                        <Check className="w-5 h-5 mr-2 text-white" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link 
                    to="/level-up"
                    className="w-full bg-white text-cresento-secondary hover:bg-gray-100 font-bold py-3 rounded-lg transition-all inline-flex items-center justify-center"
                  >
                    Sign Up for Updates
                  </Link>
                </div>
              </div>
              {selectedProduct === 'pro' && (
                <div className="absolute top-4 right-4 bg-cresento-accent text-black px-3 py-1 rounded-full text-sm font-semibold">
                  Selected
                </div>
              )}
              <div className="absolute top-4 left-4 bg-white/20 backdrop-blur text-white px-3 py-1 rounded-full text-sm font-semibold">
                Most Popular
              </div>
            </motion.div>
          </div>

          {/* Product Gallery */}
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div className="aspect-video bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentImageIndex}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.3 }}
                    className="w-full h-full bg-gradient-to-br from-cresento-primary to-cresento-dark flex items-center justify-center"
                  >
                    <Shield className="w-48 h-48 text-cresento-accent" />
                  </motion.div>
                </AnimatePresence>
              </div>
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur hover:bg-white/20 rounded-full p-2 shadow-lg transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur hover:bg-white/20 rounded-full p-2 shadow-lg transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-cresento-primary">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-cresento-accent mb-4">How We Compare</h2>
            <p className="text-xl text-cresento-light">See why Cresento is the smart choice for modern athletes. Don't settle for cheap football gear when you can have professional-grade analytics.</p>
          </motion.div>

          <div className="max-w-6xl mx-auto overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-cresento-dark">
                  <th className="py-4 px-6 text-left text-cresento-light">Feature</th>
                  <th className="py-4 px-6 text-center text-cresento-accent font-bold">Cresento</th>
                  <th className="py-4 px-6 text-center text-gray-500">Playermaker</th>
                  <th className="py-4 px-6 text-center text-gray-500">Nike/Adidas</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <motion.tr
                    key={row.feature}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    className="border-b border-gray-800"
                  >
                    <td className="py-4 px-6 text-cresento-light">{row.feature}</td>
                    <td className="py-4 px-6 text-center">
                      {typeof row.cresento === 'boolean' ? (
                        row.cresento ? (
                          <Check className="w-6 h-6 text-cresento-accent mx-auto" />
                        ) : (
                          <X className="w-6 h-6 text-gray-500 mx-auto" />
                        )
                      ) : (
                        <span className="text-cresento-accent font-semibold">{row.cresento}</span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-center">
                      {typeof row.playermaker === 'boolean' ? (
                        row.playermaker ? (
                          <Check className="w-6 h-6 text-green-500 mx-auto" />
                        ) : (
                          <X className="w-6 h-6 text-gray-500 mx-auto" />
                        )
                      ) : (
                        <span className="text-gray-400">{row.playermaker}</span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-center">
                      {typeof row.nikeAdidas === 'boolean' ? (
                        row.nikeAdidas ? (
                          <Check className="w-6 h-6 text-green-500 mx-auto" />
                        ) : (
                          <X className="w-6 h-6 text-gray-500 mx-auto" />
                        )
                      ) : (
                        <span className="text-gray-400">{row.nikeAdidas}</span>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Future Products */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-cresento-accent mb-4">Future Innovation</h2>
            <p className="text-xl text-cresento-light">Expanding beyond football to revolutionize all sports</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {futureProducts.map((product, index) => (
              <motion.div
                key={product.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass rounded-xl p-6 hover:glass-strong transition-all duration-300"
              >
                <div className="text-cresento-accent mb-4">{product.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-2">{product.title}</h3>
                <p className="text-cresento-light mb-4">{product.description}</p>
                <span className="inline-block bg-cresento-primary/50 text-cresento-accent px-3 py-1 rounded-full text-sm">
                  {product.status}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cresento-secondary to-cresento-accent text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Game?</h2>
            <p className="text-xl mb-8 text-gray-200">
              Join thousands of athletes already using Cresento to reach their peak performance
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/level-up"
                className="bg-white text-cresento-secondary hover:bg-gray-100 font-bold py-4 px-8 rounded-lg transition-all inline-block"
              >
                Sign Up for Updates
              </Link>
              <Link
                to="/contact"
                className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold py-4 px-8 rounded-lg transition-all inline-block"
              >
                Contact Sales for Teams
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Product;